class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.discoveryEl = this.querySelector('[data-predictive-search-discovery]');
    this.resultsWrapper = this.querySelector('[data-predictive-search-results-wrapper]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const detailsModal = this.closest('details-modal');
    if (detailsModal && this.discoveryEl && this.resultsWrapper) {
      const details = detailsModal.querySelector('details');
      if (details) {
        const observer = new MutationObserver((mutations) => {
          if (!details.hasAttribute('open')) return;
          mutations.forEach((m) => {
            if (m.attributeName === 'open' && details.hasAttribute('open') && !this.getQuery().length) {
              requestAnimationFrame(() => this.showDiscoveryIfEmpty());
            }
          });
        });
        observer.observe(details, { attributes: true });
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      if (this.resultsWrapper) {
        this.resultsWrapper.innerHTML = '';
      } else {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.discoveryEl && this.resultsWrapper) {
        this.showCurated();
        this.open();
      } else {
        this.close(true);
      }
      return;
    }

    if (this.discoveryEl) this.discoveryEl.style.display = 'none';
    if (this.resultsWrapper) this.resultsWrapper.style.display = '';

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      if (this.discoveryEl && this.resultsWrapper) {
        this.showCurated();
        this.open();
      }
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.discoveryEl && this.resultsWrapper) {
        this.showCurated();
        this.open();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this.discoveryEl && this.resultsWrapper && this.isOpen) {
        this.showCurated();
      } else {
        this.close(true);
      }
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product&resources[limit]=8`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  showCurated() {
    if (!this.discoveryEl || !this.resultsWrapper) return;
    this.discoveryEl.style.display = '';
    this.resultsWrapper.style.display = 'none';
    this.resultsWrapper.innerHTML = '';
    this.populateRecentlyViewed();
    this.setAttribute('results', true);
  }

  showDiscoveryIfEmpty() {
    if (this.getQuery().length) return;
    if (this.discoveryEl && this.resultsWrapper) {
      this.showCurated();
      this.open();
    }
  }

  getRecentlyViewedProductsFromStorage() {
    try {
      const raw = localStorage.getItem('recently_viewed_products') || localStorage.getItem('viewedProducts');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  normalizeRecentItem(p) {
    if (p == null) return null;
    if (typeof p === 'string') return { handle: p, title: p.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), featured_image: null, timestamp: 0 };
    const raw = p.product && typeof p.product === 'object' ? p.product : p;
    const handle = raw.handle || p.handle || raw.id?.toString?.() || '';
    const title = raw.title || raw.name || p.title || p.name || (handle ? handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');
    let imgSrc = raw.featured_image_url || p.featured_image_url || '';
    if (!imgSrc) {
      const img = raw.featured_image ?? raw.image ?? p.featured_image ?? p.image;
      if (typeof img === 'string') imgSrc = img;
      else if (img && typeof img === 'object') imgSrc = img.src || img.url || (img.preview_image && (img.preview_image.src || img.preview_image.url)) || '';
    }
    return { handle, title: title || 'Product', featured_image: imgSrc, timestamp: p.viewedAt ?? p.timestamp ?? raw.timestamp ?? 0 };
  }

  populateRecentlyViewed() {
    const container = this.discoveryEl?.querySelector('[data-predictive-search-recently-viewed]');
    const emptyEl = this.discoveryEl?.querySelector('[data-predictive-search-recently-viewed-empty]');
    const sectionEl = this.discoveryEl?.querySelector('[data-predictive-search-recently-viewed-section]');
    if (!container) return;
    try {
      const stored = this.getRecentlyViewedProductsFromStorage();
      if (!Array.isArray(stored)) {
        if (emptyEl) emptyEl.style.display = '';
        if (sectionEl) sectionEl.style.display = 'none';
        return;
      }
      const isOnProductPage = window.location.pathname.includes('/products/');
      const currentHandle = isOnProductPage ? window.location.pathname.split('/products/')[1].split('?')[0].split('/')[0] : null;
      const filtered = currentHandle ? stored.filter((p) => p && (typeof p === 'string' ? p !== currentHandle : p.handle !== currentHandle)) : stored;
      const normalized = filtered.map((p) => this.normalizeRecentItem(p)).filter(Boolean);
      const sorted = [...normalized].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      const list = sorted.slice(0, 3);

      container.querySelectorAll('.predictive-search__discovery-recent-item').forEach((n) => n.remove());

      if (list.length === 0) {
        if (emptyEl) emptyEl.style.display = '';
        if (sectionEl) sectionEl.style.display = 'none';
        return;
      }

      if (sectionEl) sectionEl.style.display = '';
      if (emptyEl) emptyEl.style.display = 'none';

      list.forEach((p) => {
        const handle = p.handle || '';
        const href = `/products/${handle}`;
        let imgSrc = p.featured_image || '';
        const widthParam = imgSrc ? (imgSrc.includes('?') ? '&width=100' : '?width=100') : '';
        const title = (p.title || 'Product').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        const imgHtml = imgSrc
          ? `<img class="predictive-search__image" src="${imgSrc}${widthParam}" alt="${title}" width="50" height="50" loading="lazy">`
          : `<img class="predictive-search__image" data-recently-viewed-placeholder data-handle="${handle.replace(/"/g, '&quot;')}" alt="${title}" width="50" height="50" loading="lazy">`;
        const li = document.createElement('li');
        li.className = 'predictive-search__discovery-recent-item predictive-search__list-item';
        li.innerHTML = `<a href="${href}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">${imgHtml}<div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading h5">${title}</p></div></a>`;
        container.appendChild(li);
      });

      this.fetchMissingRecentImages(container);
    } catch (e) {
      if (emptyEl) emptyEl.style.display = '';
      if (sectionEl) sectionEl.style.display = 'none';
    }
  }

  fetchMissingRecentImages(container) {
    const placeholders = container?.querySelectorAll('img[data-recently-viewed-placeholder]');
    if (!placeholders?.length) return;
    placeholders.forEach((img) => {
      const handle = img.getAttribute('data-handle');
      if (!handle) return;
      fetch(`/products/${encodeURIComponent(handle)}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((product) => {
          if (!product?.featured_image) return;
          const url = typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image?.src || product.featured_image?.url || '');
          if (url) {
            img.src = url.includes('?') ? `${url}&width=100` : `${url}?width=100`;
            img.removeAttribute('data-recently-viewed-placeholder');
            img.removeAttribute('data-handle');
          }
        })
        .catch(() => {});
    });
  }

  renderSearchResults(resultsMarkup) {
    if (this.resultsWrapper) {
      this.resultsWrapper.innerHTML = resultsMarkup;
      this.resultsWrapper.style.display = '';
      if (this.discoveryEl) this.discoveryEl.style.display = 'none';
    } else {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('overflow-hidden');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.maxHeight = '';
    this.predictiveSearchResults.style.display = 'none';
    const anyOpen = Array.from(this.allPredictiveSearchInstances || []).some((el) => el.hasAttribute('open'));
    if (!anyOpen) document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('predictive-search', PredictiveSearch);
